import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Email as EmailIcon,
  Folder as FolderIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  AutoAwesome as AIIcon,
  Keyboard as KeyboardIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  Menu as MenuIcon,
  ArrowUpward as TopIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useBranding } from '../../utils/BrandingContext';

/* ────────────────────────── types ────────────────────────── */

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  badge?: string;
  content: React.ReactNode;
}

/* ────────────────────────── component ────────────────────── */

const HelpPage: React.FC = () => {
  const theme = useTheme();
  const { branding } = useBranding();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const scrollToTop = () => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const handleSectionClick = (id: string) => {
    setActiveSection(id);
    setDrawerOpen(false);
    scrollToTop();
  };

  /* ── shared sub-components ── */
  const StepItem: React.FC<{ step: number; text: string }> = ({ step, text }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.6 }}>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0, mt: 0.1,
          bgcolor: theme.palette.primary.main, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700,
        }}
      >
        {step}
      </Box>
      <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>{text}</Typography>
    </Box>
  );

  const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      sx={{
        mt: 2, p: 1.5, borderRadius: 1.5,
        bgcolor: alpha(theme.palette.info.main, 0.08),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
      }}
    >
      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary }}>
        💡 <strong>Tip:</strong> {children}
      </Typography>
    </Box>
  );

  const SectionH2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem', mt: 3, mb: 1.5, color: theme.palette.text.primary }}>
      {children}
    </Typography>
  );

  const SectionH3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem', mt: 2.5, mb: 1, color: theme.palette.text.primary }}>
      {children}
    </Typography>
  );

  const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.75, fontSize: '0.9rem' }}>
      {children}
    </Typography>
  );

  const BulletList: React.FC<{ items: string[]; icon?: 'check' | 'arrow' }> = ({ items, icon = 'check' }) => (
    <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
      {items.map((item, i) => (
        <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.3 }}>
          {icon === 'check'
            ? <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main, mt: 0.2, flexShrink: 0 }} />
            : <ArrowIcon sx={{ fontSize: 16, color: theme.palette.primary.main, mt: 0.3, flexShrink: 0 }} />}
          <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );

  const InfoTable: React.FC<{ headers: string[]; rows: string[][] }> = ({ headers, rows }) => (
    <Box
      component="table"
      sx={{
        width: '100%', borderCollapse: 'collapse', my: 1.5, fontSize: '0.85rem',
        '& th, & td': {
          border: `1px solid ${theme.palette.divider}`, px: 1.5, py: 1, textAlign: 'left',
        },
        '& th': {
          bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03),
          fontWeight: 600, fontSize: '0.85rem',
        },
        '& td': { fontSize: '0.85rem' },
      }}
    >
      <thead>
        <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
        ))}
      </tbody>
    </Box>
  );

  const KeyboardShortcut: React.FC<{ shortcut: string; action: string }> = ({ shortcut, action }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="kbd"
        sx={{
          px: 1, py: 0.25, borderRadius: 0.5, fontSize: '0.8rem', fontWeight: 600,
          fontFamily: 'monospace', minWidth: 32, textAlign: 'center',
          bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
          border: `1px solid ${isDark ? alpha('#fff', 0.15) : alpha('#000', 0.12)}`,
        }}
      >
        {shortcut}
      </Box>
      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{action}</Typography>
    </Box>
  );

  /* ══════════════════════════════════════════════════════════════
     SECTIONS
     ══════════════════════════════════════════════════════════════ */

  const sections: HelpSection[] = [
    /* ─── 1. Getting Started ─── */
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <RocketIcon />,
      badge: 'Start Here',
      content: (
        <Box>
          <SectionH2>Welcome to {branding.companyName || 'CruvzMail'}</SectionH2>
          <Para>
            {branding.companyName || 'CruvzMail'} is your secure, self-hosted email platform built for professionals
            and organizations. Your data stays on your own servers — giving you complete privacy, security, and
            compliance control.
          </Para>

          <SectionH3>What you can do</SectionH3>
          <BulletList items={[
            'Send and receive emails with rich formatting & attachments',
            'Organize messages with folders, labels, and smart filters',
            'Manage contacts, calendars, tasks, and notes in one place',
            'Use AI-powered tools for drafting and summarizing emails',
            'Track email delivery status in real-time',
            'Customize your experience with themes, signatures, and settings',
          ]} />

          <SectionH2>Signing In</SectionH2>
          <StepItem step={1} text="Open your browser and navigate to your CruvzMail URL (e.g., mail.yourdomain.com)." />
          <StepItem step={2} text="Enter your full email address as the username." />
          <StepItem step={3} text="Type your password and click Sign In." />
          <StepItem step={4} text="You'll land on your Inbox — ready to go!" />

          <TipBox>
            If you forget your password, use the "Forgot Password" link on the login page.
            Your administrator can also reset it from the admin panel.
          </TipBox>

          <SectionH2>Interface Overview</SectionH2>
          <Para>The left sidebar is your main navigation hub:</Para>
          <InfoTable
            headers={['Section', 'Purpose']}
            rows={[
              ['Mail', 'Access Inbox, Sent, Drafts, and other mail folders'],
              ['Compose', 'Open a new email compose tab'],
              ['Calendar', 'View and manage your schedule'],
              ['Tasks', 'Track to-dos and action items'],
              ['Notes', 'Quick notes and memos'],
              ['Contacts', 'Manage your address book'],
              ['Streams', 'Activity streams and updates'],
              ['Bookmarks', 'Saved emails for quick access'],
              ['Analytics', 'Email usage statistics'],
              ['AI Assistant', 'AI-powered email help'],
              ['Settings', 'Customize your experience'],
            ]}
          />

          <SectionH2>Setting Up Your Account</SectionH2>
          <SectionH3>1. Update Your Profile</SectionH3>
          <Para>Click your avatar in the top-right → Profile. Add your display name, job title, and profile photo.</Para>

          <SectionH3>2. Create Your Email Signature</SectionH3>
          <Para>Go to Settings → Signature. Use the rich text editor to add your name, title, phone, and company details. Your signature will automatically appear in new emails.</Para>

          <SectionH3>3. Choose Your Theme</SectionH3>
          <Para>Settings → System. Pick Light, Dark, or System theme, and choose an accent color.</Para>

          <SectionH3>4. Enable Notifications</SectionH3>
          <Para>Settings → Notification. Enable desktop notifications to get alerts for new emails even when the tab is in the background.</Para>

          <TipBox>
            Press <strong>C</strong> from anywhere to quickly open a new compose tab. Star important emails and find them all in the Starred folder.
          </TipBox>
        </Box>
      ),
    },

    /* ─── 2. Composing & Sending ─── */
    {
      id: 'composing-email',
      title: 'Composing & Sending Email',
      icon: <EmailIcon />,
      content: (
        <Box>
          <SectionH2>Creating a New Email</SectionH2>
          <Para>
            Start composing via the Compose button in the sidebar, by pressing <strong>C</strong> on your keyboard, or
            navigating to /compose. The compose panel opens as a tab — you can have multiple compose tabs open simultaneously.
          </Para>

          <SectionH3>Recipients</SectionH3>
          <InfoTable
            headers={['Field', 'Purpose']}
            rows={[
              ['To', 'Primary recipients who should take action'],
              ['Cc', 'Recipients who should be informed (visible to all)'],
              ['Bcc', 'Hidden recipients — other recipients cannot see them'],
            ]}
          />
          <Para>Start typing to see auto-complete suggestions from your contacts. Press Enter to add an address.</Para>

          <SectionH3>Rich Text Editor</SectionH3>
          <BulletList icon="arrow" items={[
            'Bold, Italic, Strikethrough, Underline',
            'Font family and font size selection (including custom sizes)',
            'Text color and highlight color',
            'Bullet lists, numbered lists, and block quotes',
            'Links, inline images, and horizontal rules',
            'Code blocks',
          ]} />

          <SectionH2>Reply, Reply All & Forward</SectionH2>
          <InfoTable
            headers={['Action', 'Behavior']}
            rows={[
              ['Reply', 'Respond to the sender only. Subject prefixed with "Re:"'],
              ['Reply All', 'Respond to sender + all To/Cc recipients'],
              ['Forward', 'Send the email to new recipients. Subject prefixed with "Fwd:"'],
              ['Edit as New', 'Open a fresh compose with original To, Cc, subject, and body — no prefix'],
            ]}
          />
          <Para>
            When replying, your signature is placed <strong>above</strong> the quoted content, following standard business email convention.
          </Para>

          <SectionH2>Working with Attachments</SectionH2>
          <InfoTable
            headers={['Limit', 'Value']}
            rows={[
              ['Maximum file size', '25 MB per file'],
              ['Maximum files per email', '10'],
              ['Maximum total size', '25 MB per message'],
            ]}
          />
          <Para>
            Click the paperclip icon or drag-and-drop files into the compose area. Supported formats include
            PDF, DOCX, XLSX, images (JPG/PNG/GIF/SVG), ZIP, and more.
          </Para>
          <Para>
            When viewing received attachments, image files show inline previews. Click the download icon to save any attachment.
          </Para>

          <SectionH2>Sending Rate Limits</SectionH2>
          <InfoTable
            headers={['Limit', 'Value']}
            rows={[
              ['Daily send limit per user', '500 emails'],
              ['Hourly send limit per user', '100 emails'],
              ['Max recipients per email', '100'],
            ]}
          />

          <SectionH2>Email Signatures</SectionH2>
          <StepItem step={1} text="Go to Settings → Signature." />
          <StepItem step={2} text="Use the rich editor to build your signature with text, images, and links." />
          <StepItem step={3} text="Click Save. It auto-appears on new emails and above quoted content on replies." />
          <TipBox>
            Keep signatures concise (4-6 lines). Include name, role, phone, and website. Use logos at a reasonable size (under 200px wide).
          </TipBox>

          <SectionH2>Draft Management</SectionH2>
          <BulletList items={[
            'Compose tabs auto-save — close without sending and the email moves to Drafts',
            'Open your Drafts folder and click any draft to continue editing',
            'Multiple compose tabs auto-save independently',
          ]} />
        </Box>
      ),
    },

    /* ─── 3. Organization & Folders ─── */
    {
      id: 'organization',
      title: 'Organization & Folders',
      icon: <FolderIcon />,
      content: (
        <Box>
          <SectionH2>System Folders</SectionH2>
          <InfoTable
            headers={['Folder', 'Purpose']}
            rows={[
              ['Inbox', 'Where all incoming messages arrive'],
              ['Sent', 'Copies of every email you send'],
              ['Drafts', 'Unfinished emails saved for later'],
              ['Starred', 'Emails marked with a star'],
              ['Spam', 'Messages flagged as unwanted'],
              ['Trash', 'Deleted messages — auto-purged after 30 days'],
              ['Archive', 'Messages removed from inbox but preserved'],
            ]}
          />

          <SectionH2>Custom Folders</SectionH2>
          <StepItem step={1} text='Go to Settings → Folders.' />
          <StepItem step={2} text='Click "Create Folder" and enter a name.' />
          <StepItem step={3} text='Click Save. Your folder appears in the sidebar.' />

          <SectionH3>Moving Emails</SectionH3>
          <Para>
            Use the three-dot menu (⋮) → Move to folder, or select emails and use the Move button in the toolbar.
          </Para>

          <SectionH2>Labels</SectionH2>
          <Para>
            Labels let you categorize emails without moving them. A single email can have multiple labels.
            Manage labels in Settings → Labels — create, rename, delete, and assign colors.
          </Para>
          <Para>Apply labels via the three-dot menu → Add label.</Para>

          <SectionH2>Stars</SectionH2>
          <BulletList items={[
            'Click the star icon (☆) on any email row, or press S while viewing',
            'View all starred emails in the Starred folder',
            'Click a filled star (★) to un-star',
          ]} />

          <SectionH2>Archive & Delete</SectionH2>
          <SectionH3>Archive</SectionH3>
          <Para>
            Archive removes emails from Inbox without deleting. Press <strong>E</strong> or use the Archive button.
            Find archived emails in the Archive folder or via search.
          </Para>
          <SectionH3>Delete</SectionH3>
          <Para>
            Press <strong>#</strong> or use the Delete button. Emails go to Trash and are permanently removed after 30 days.
            Recover from Trash by moving back to Inbox.
          </Para>

          <SectionH2>Search</SectionH2>
          <Para>
            Click the search icon or press <strong>/</strong> to focus search. Type keywords to search across sender, recipient,
            subject, and body. Results update in real-time across all folders including Archive and Sent.
          </Para>

          <SectionH2>Marking Emails</SectionH2>
          <BulletList icon="arrow" items={[
            'Mark as unread: Three-dot menu → Mark as unread, or press U',
            'Report spam: Three-dot menu → Report spam (moves to Spam folder)',
            'Not spam: From Spam folder, open email → Not spam (moves to Inbox)',
          ]} />

          <SectionH2>Bulk Actions</SectionH2>
          <Para>Select multiple emails with checkboxes, then use toolbar buttons for Delete, Archive, Move, Mark as read/unread, and Star/Unstar.</Para>
        </Box>
      ),
    },

    /* ─── 4. Security & Hosting ─── */
    {
      id: 'security',
      title: 'Security & Email Hosting',
      icon: <SecurityIcon />,
      content: (
        <Box>
          <SectionH2>Self-Hosted Infrastructure</SectionH2>
          <Para>
            With {branding.companyName || 'CruvzMail'}, your email data is stored on servers your organization controls — not on
            a third-party cloud. This gives you full data ownership, privacy compliance, and no vendor lock-in.
          </Para>

          <SectionH2>Email Authentication Protocols</SectionH2>

          <SectionH3>SPF (Sender Policy Framework)</SectionH3>
          <Para>
            SPF tells receiving servers which servers are authorized to send email from your domain. Your DNS contains
            an SPF record listing allowed sending servers. Receiving servers verify this on every incoming email.
          </Para>

          <SectionH3>DKIM (DomainKeys Identified Mail)</SectionH3>
          <Para>
            DKIM adds a digital signature to every outgoing email, proving it hasn't been tampered with in transit.
            Your mail server signs with a private key; the public key is in DNS for receivers to verify.
          </Para>

          <SectionH3>DMARC</SectionH3>
          <Para>
            DMARC ties SPF and DKIM together and tells receiving servers what to do when authentication fails:
            <strong> none</strong> (monitor), <strong> quarantine</strong> (send to spam), or <strong> reject</strong> (block entirely).
          </Para>

          <SectionH2>TLS Encryption</SectionH2>
          <Para>
            TLS encrypts email in transit between mail servers. {branding.companyName || 'CruvzMail'} attempts TLS for every outgoing
            email and accepts encrypted inbound connections. The delivery tracking panel shows TLS status for each sent email.
          </Para>

          <SectionH2>Email Delivery Tracking</SectionH2>
          <Para>
            Every sent email includes built-in delivery tracking. Open any email in your Sent folder to see the
            delivery status panel with:
          </Para>
          <InfoTable
            headers={['Data Point', 'Description']}
            rows={[
              ['Delivery Status', 'Delivered, Pending, or Failed'],
              ['SMTP Response', 'Receiving server\'s response code and message'],
              ['TLS Encryption', 'Whether the connection was encrypted'],
              ['Delivered At', 'Timestamp of acceptance by recipient server'],
              ['Message Size', 'Total size including attachments'],
              ['Recipients', 'Per-recipient status (accepted/rejected/pending)'],
            ]}
          />
          <TipBox>
            Click "Show Details" in the delivery panel for full SMTP response info and recipient grid.
          </TipBox>

          <SectionH2>DNS Records Required</SectionH2>
          <InfoTable
            headers={['Record', 'Purpose']}
            rows={[
              ['MX', 'Directs incoming email to your mail server'],
              ['SPF (TXT)', 'Authorizes your server to send email'],
              ['DKIM (TXT)', 'Publishes your email signing key'],
              ['DMARC (TXT)', 'Sets authentication failure policy'],
              ['A', 'Points your mail domain to your server IP'],
            ]}
          />

          <SectionH2>Session Security</SectionH2>
          <BulletList items={[
            'JWT-based session management with automatic token refresh',
            'Passwords are hashed — never stored in plain text',
            'Access tokens have limited lifespan; refresh happens silently in background',
          ]} />

          <SectionH2>Spam & Phishing Protection</SectionH2>
          <Para>
            Incoming emails are analyzed by Rspamd for spam patterns, sender reputation, authentication failures,
            suspicious links, and bayesian filtering. Report spam via the three-dot menu to train the filter.
          </Para>

          <SectionH3>Security Best Practices</SectionH3>
          <BulletList items={[
            'Use a strong, unique password (12+ characters with letters, numbers, symbols)',
            'Log out on shared devices',
            'Watch for phishing — don\'t click suspicious links or open unexpected attachments',
            'Report suspicious emails with "Report spam"',
          ]} />
        </Box>
      ),
    },

    /* ─── 5. Productivity Features ─── */
    {
      id: 'productivity',
      title: 'Productivity Features',
      icon: <AIIcon />,
      content: (
        <Box>
          <SectionH2>Calendar</SectionH2>
          <Para>Access from the Calendar icon in the sidebar. View daily, weekly, or monthly schedules.</Para>
          <BulletList items={[
            'Create events with title, time, location, and description',
            'Schedule meetings directly from email via three-dot menu → Schedule Meeting',
            'Color-coded events for easy visual scanning',
          ]} />

          <SectionH2>Tasks</SectionH2>
          <Para>Track to-dos from the Tasks icon. Create tasks with priority levels (High, Medium, Low) and due dates.</Para>
          <BulletList items={[
            'Convert any email into a task for follow-up tracking',
            'Mark tasks complete with a checkbox',
            'Set due dates and priority levels',
          ]} />

          <SectionH2>Contacts</SectionH2>
          <Para>Built-in address book accessible from the Contacts icon.</Para>
          <BulletList items={[
            'Add contacts manually or save from received emails',
            'Organize into custom groups (Team, Clients, Vendors, etc.)',
            'Auto-complete suggestions when composing emails',
            'View contact details and email history',
          ]} />

          <SectionH2>Notes</SectionH2>
          <Para>
            Quick notes space within your email app — perfect for meeting notes, reference info, and draft ideas.
            Supports rich text formatting.
          </Para>

          <SectionH2>Bookmarks</SectionH2>
          <Para>
            Save specific emails for quick reference — separate from starring. Access all bookmarks from the
            Bookmarks section in the sidebar.
          </Para>

          <SectionH2>Streams</SectionH2>
          <Para>
            Activity feed showing recent email events and conversation updates. Get a timeline
            overview of what's happening across your mailbox.
          </Para>

          <SectionH2>Analytics</SectionH2>
          <Para>Email usage insights including send/receive volume, response times, peak hours, and top contacts.</Para>

          <SectionH2>AI Assistant</SectionH2>
          <Para>Access from the AI icon in the sidebar. Type natural language requests:</Para>
          <InfoTable
            headers={['Capability', 'Example']}
            rows={[
              ['Draft emails', '"Write a follow-up about the project deadline"'],
              ['Summarize threads', '"Summarize this conversation in 3 bullet points"'],
              ['Rewrite tone', '"Make this more formal" or "Make it casual"'],
              ['Generate subjects', '"Suggest a subject for this budget review email"'],
              ['Translate', '"Translate this email to Spanish"'],
            ]}
          />
          <TipBox>
            Be specific with AI requests: "Write a 3-sentence reply declining the meeting" works better than "reply to this".
          </TipBox>

          <SectionH2>Mobile & Multi-Device</SectionH2>
          <Para>
            {branding.companyName || 'CruvzMail'}'s interface is fully responsive across desktop, tablet, and phone. No app
            installation required — just open your browser.
          </Para>

          <SectionH3>Add to Home Screen</SectionH3>
          <Para>
            On mobile, tap your browser menu → "Add to Home Screen" for a native app-like experience.
          </Para>

          <SectionH3>Third-Party Email Clients</SectionH3>
          <InfoTable
            headers={['Protocol', 'Server', 'Port']}
            rows={[
              ['IMAP (incoming)', 'mail.yourdomain.com', '993 (SSL/TLS)'],
              ['SMTP (outgoing)', 'mail.yourdomain.com', '587 (STARTTLS)'],
            ]}
          />
          <Para>Works with Outlook, Thunderbird, Apple Mail, and any standard IMAP/SMTP client. Use your full email address as the username.</Para>

          <SectionH3>Desktop Notifications</SectionH3>
          <Para>Enable in Settings → Notification. You'll receive browser notifications and hear a sound when new mail arrives, even in background tabs.</Para>
        </Box>
      ),
    },

    /* ─── 6. Keyboard Shortcuts ─── */
    {
      id: 'shortcuts',
      title: 'Keyboard Shortcuts',
      icon: <KeyboardIcon />,
      content: (
        <Box>
          <SectionH2>Keyboard Shortcuts</SectionH2>
          <Para>Speed up your workflow with shortcuts (active when not typing in a text field):</Para>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, my: 2 }}>
            <KeyboardShortcut shortcut="C" action="New compose" />
            <KeyboardShortcut shortcut="/" action="Focus search" />
            <KeyboardShortcut shortcut="R" action="Reply" />
            <KeyboardShortcut shortcut="A" action="Reply all" />
            <KeyboardShortcut shortcut="F" action="Forward" />
            <KeyboardShortcut shortcut="E" action="Archive" />
            <KeyboardShortcut shortcut="#" action="Delete" />
            <KeyboardShortcut shortcut="S" action="Toggle star" />
            <KeyboardShortcut shortcut="U" action="Mark unread" />
            <KeyboardShortcut shortcut="Esc" action="Close / Go back" />
          </Box>
          <TipBox>Keyboard shortcuts can be customized from Settings → Hotkeys.</TipBox>
        </Box>
      ),
    },

    /* ─── 7. Admin & User Management ─── */
    {
      id: 'admin',
      title: 'Administration',
      icon: <GroupIcon />,
      content: (
        <Box>
          <SectionH2>User Management</SectionH2>
          <SectionH3>Adding New Users</SectionH3>
          <StepItem step={1} text="Open the admin panel and go to Users." />
          <StepItem step={2} text="Click Add User." />
          <StepItem step={3} text="Enter full email address, display name, and initial password." />
          <StepItem step={4} text="Assign a role (Admin, Manager, or User) and click Create." />

          <SectionH3>User Roles</SectionH3>
          <InfoTable
            headers={['Role', 'Capabilities']}
            rows={[
              ['Admin', 'Full access — users, domains, settings, branding'],
              ['Manager', 'View/manage users, limited system settings'],
              ['User', 'Standard email access — own mailbox only'],
            ]}
          />

          <SectionH2>Domain Configuration</SectionH2>
          <StepItem step={1} text="In admin panel or setup wizard, enter your domain name." />
          <StepItem step={2} text="Follow DNS verification to prove ownership." />
          <StepItem step={3} text="Configure MX, SPF, DKIM, and DMARC records." />
          <StepItem step={4} text="Verify each record (may take 15 min – 48 hrs to propagate)." />

          <SectionH2>Branding & Customization</SectionH2>
          <InfoTable
            headers={['Setting', 'Description']}
            rows={[
              ['Logo', 'Company logo on login and sidebar'],
              ['Favicon', 'Custom browser tab icon'],
              ['Company name', 'Displayed throughout the interface'],
              ['Primary color', 'Main interface color'],
              ['Accent color', 'Highlight and interactive elements'],
              ['Login title/subtitle', 'Custom login page text'],
              ['Support email', 'Displayed in help sections'],
            ]}
          />
          <Para>
            Multi-domain: each domain can have its own branding — users see appropriate branding for their domain.
          </Para>

          <SectionH2>Settings Reference</SectionH2>
          <BulletList icon="arrow" items={[
            'Settings → System: Theme mode and accent color',
            'Settings → Compose: Default font and size',
            'Settings → Signature: Email signature editor',
            'Settings → Notification: Desktop notification preferences',
            'Settings → Folders: Create and manage custom folders',
            'Settings → Labels: Create and manage labels',
            'Settings → Out of Office: Vacation auto-reply',
            'Settings → Mail Accounts: Connected accounts',
            'Settings → Hotkeys: Keyboard shortcut customization',
          ]} />
        </Box>
      ),
    },

    /* ─── 8. Troubleshooting ─── */
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: <BuildIcon />,
      content: (
        <Box>
          <SectionH2>Login Issues</SectionH2>
          <BulletList icon="arrow" items={[
            'Verify you\'re entering your full email address (not just the username part)',
            'Check Caps Lock is off',
            'Use the "Forgot Password" link to reset',
            'Contact your admin to verify your account is active',
            'Clear browser cache/cookies if session keeps expiring',
            'Ensure your system clock is accurate (affects token validation)',
          ]} />

          <SectionH2>Email Delivery Problems</SectionH2>
          <SectionH3>Emails going to recipients' spam</SectionH3>
          <BulletList icon="arrow" items={[
            'Verify SPF record includes your mail server IP',
            'Confirm DKIM is properly configured and signing is active',
            'Set up a DMARC policy (start with p=none for monitoring)',
            'Check server IP reputation for blacklist status',
            'Avoid spam patterns: all-caps subjects, excessive links, large images',
            'Check delivery tracking panel for specific SMTP responses',
          ]} />

          <SectionH3>Emails not being sent</SectionH3>
          <BulletList icon="arrow" items={[
            'Check daily/hourly sending limits (500/day, 100/hour)',
            'Verify recipient email address format',
            'Check delivery tracking for error details',
            'Ensure mail server is running',
          ]} />

          <SectionH3>Not receiving emails</SectionH3>
          <BulletList icon="arrow" items={[
            'Check Spam folder for false positives',
            'Verify MX record points to correct mail server',
            'Ask sender to check their delivery/bounce status',
            'Contact admin to check mail server logs',
          ]} />

          <SectionH2>Attachment Issues</SectionH2>
          <BulletList icon="arrow" items={[
            'File must be under 25 MB per file, 25 MB total, max 10 files',
            'Try a different format if upload is rejected',
            'For download issues, click the download icon to save locally first',
            'If attachment appears corrupted, ask sender to resend',
          ]} />

          <SectionH2>Performance Issues</SectionH2>
          <BulletList icon="arrow" items={[
            'Clear browser cache (Ctrl+Shift+Delete)',
            'Close unnecessary browser tabs',
            'Hard refresh (Ctrl+Shift+R) to reload without cache',
            'Disable browser extensions temporarily',
            'Check internet connection for real-time update issues',
          ]} />

          <SectionH2>Notification Issues</SectionH2>
          <BulletList icon="arrow" items={[
            'Ensure notifications are enabled in Settings → Notification',
            'Check browser notification permissions for CruvzMail domain',
            'Verify OS allows notifications from your browser',
          ]} />

          <SectionH2>Getting Help</SectionH2>
          <Para>
            Contact your organization's email administrator or reach out to{' '}
            <strong>{branding.supportEmail || 'support@cruvzmail.com'}</strong>. When reporting issues, include:
            what you were doing, what happened, any error messages, and your browser version.
          </Para>
        </Box>
      ),
    },
  ];

  /* ══════════════════════════════════════════════════════════════
     SIDEBAR NAV
     ══════════════════════════════════════════════════════════════ */

  const navContent = (
    <Box sx={{ py: 1 }}>
      <Box sx={{ px: 2, py: 1.5 }}>
        {branding.logo ? (
          <Box component="img" src={branding.logo} alt={branding.companyName} sx={{ height: 28, objectFit: 'contain' }} />
        ) : (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: '-0.02em' }}>
            {branding.companyName || 'CruvzMail'}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem', mt: 0.25 }}>
          Help Center
        </Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      <List dense disablePadding>
        {sections.map((s) => (
          <ListItemButton
            key={s.id}
            selected={activeSection === s.id}
            onClick={() => handleSectionClick(s.id)}
            sx={{
              mx: 1, borderRadius: 1, mb: 0.25,
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: activeSection === s.id ? theme.palette.primary.main : theme.palette.text.secondary }}>
              {s.icon}
            </ListItemIcon>
            <ListItemText
              primary={s.title}
              primaryTypographyProps={{
                fontSize: '0.83rem',
                fontWeight: activeSection === s.id ? 600 : 400,
                color: activeSection === s.id ? theme.palette.primary.main : theme.palette.text.primary,
              }}
            />
            {s.badge && (
              <Chip
                label={s.badge}
                size="small"
                color={s.badge === 'Start Here' ? 'primary' : 'success'}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
              />
            )}
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  const activeContent = sections.find((s) => s.id === activeSection);

  /* ══════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════ */

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Sidebar — desktop */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 250, flexShrink: 0, borderRight: `1px solid ${theme.palette.divider}`,
            overflow: 'auto', bgcolor: theme.palette.background.paper,
          }}
        >
          {navContent}
        </Paper>
      )}

      {/* Sidebar — mobile drawer */}
      {isMobile && (
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 260 } }}>
          {navContent}
        </Drawer>
      )}

      {/* Main content area */}
      <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', bgcolor: theme.palette.background.default }}>
        {/* Mobile header bar */}
        {isMobile && (
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <IconButton size="small" onClick={() => setDrawerOpen(true)}><MenuIcon /></IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{activeContent?.title}</Typography>
          </Box>
        )}

        <Box sx={{ maxWidth: 780, mx: 'auto', p: { xs: 2, sm: 3.5 } }}>
          {/* Section title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{ color: theme.palette.primary.main }}>{activeContent?.icon}</Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              {activeContent?.title}
            </Typography>
            {activeContent?.badge && (
              <Chip
                label={activeContent.badge}
                size="small"
                color={activeContent.badge === 'Start Here' ? 'primary' : 'success'}
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
              />
            )}
          </Box>
          <Divider sx={{ mb: 3 }} />

          {/* Section content */}
          {activeContent?.content}

          {/* Navigation footer */}
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 4 }}>
            {(() => {
              const idx = sections.findIndex((s) => s.id === activeSection);
              const prev = idx > 0 ? sections[idx - 1] : null;
              const next = idx < sections.length - 1 ? sections[idx + 1] : null;
              return (
                <>
                  {prev ? (
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', color: theme.palette.primary.main, fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => handleSectionClick(prev.id)}
                    >
                      ← {prev.title}
                    </Typography>
                  ) : <Box />}
                  {next ? (
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', color: theme.palette.primary.main, fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => handleSectionClick(next.id)}
                    >
                      {next.title} →
                    </Typography>
                  ) : <Box />}
                </>
              );
            })()}
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', pb: 3, color: theme.palette.text.secondary }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {branding.copyrightText || `© ${new Date().getFullYear()} CruvzMail. All rights reserved.`}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5, opacity: 0.7 }}>
              Questions? Contact {branding.supportEmail || 'support@cruvzmail.com'}
            </Typography>
          </Box>
        </Box>

        {/* Scroll to top button */}
        <Tooltip title="Back to top">
          <IconButton
            onClick={scrollToTop}
            sx={{
              position: 'fixed', bottom: 24, right: 24,
              bgcolor: theme.palette.primary.main, color: '#fff',
              '&:hover': { bgcolor: theme.palette.primary.dark },
              boxShadow: 3,
            }}
          >
            <TopIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default HelpPage;
